// Niektóre implementacje SpeechRecognition (Android/Chrome, hałaśliwe otoczenie,
// continuous=true) potrafią wielokrotnie "poprawiać się" w trakcie tej samej
// wypowiedzi, zwracając serię KOLEJNYCH wyników finalnych, z których każdy
// następny jest dłuższą/poprawioną wersją poprzedniego (a nie kontynuacją):
// "ha" → "hala produ" → "hala produkcyjna b awaria..." itd. Naiwne sklejanie
// wszystkich wyników finalnych ze sobą (jak dotąd) produkuje bełkot z
// powtórzonym początkiem zdania wielokrotnie. Właściwe podejście: gdy kolejny
// finalny segment zaczyna się od poprzedniego (albo odwrotnie) — to rewizja,
// zostaw tylko dłuższą wersję zamiast dokładać obie.
//
// Uwaga: porównanie idzie po CAŁEJ dotychczasowej historii segmentów, nie tylko
// po ostatnim — silnik czasem powtarza wcześniejszy, już zamknięty fragment PO
// tym, jak w międzyczasie pojawił się inny, nowy segment (nie tylko rozbudowuje
// bieżący). Porównanie tylko z "ostatnim" takiego przypadku by nie złapało.
//
// Uwaga 2: dokładny prefiks nie wystarcza — silnik czasem powtarza to samo
// zdanie z INNYM zgadywanym zakończeniem przy każdej próbie (np. "...i nowy
// sto" / "...i nowy stylisty" / "...i nowy stolicy serwis" — trzy próby
// dokończenia tego samego słowa). Dlatego liczymy udział wspólnych słów od
// początku (nie tylko 100%-owy prefiks) — jeśli jest wysoki, to wciąż ta sama
// próba, a nie nowa, niezależna treść.
const REVISION_SIMILARITY_THRESHOLD = 0.7

function commonPrefixRatio(aWords: string[], bWords: string[]): number {
  const n = Math.min(aWords.length, bWords.length)
  if (n === 0) return 0
  let common = 0
  for (let i = 0; i < n; i++) {
    if (aWords[i] !== bWords[i]) break
    common++
  }
  return common / n
}

export function mergeFinalSegments(segments: string[]): string {
  const merged: string[] = []
  for (const raw of segments) {
    const t = raw.trim()
    if (!t) continue
    const bWords = t.toLowerCase().split(/\s+/)

    // Czy nowy segment jest rewizją JAKIEGOKOLWIEK wcześniejszego (nie tylko ostatniego)?
    let handled = false
    for (let i = 0; i < merged.length; i++) {
      const aWords = merged[i].toLowerCase().split(/\s+/)
      if (aWords.join(' ') === bWords.join(' ')) { handled = true; break } // dokładny powtórzony fragment — odrzuć
      if (commonPrefixRatio(aWords, bWords) >= REVISION_SIMILARITY_THRESHOLD) {
        // rewizja tej samej wypowiedzi — zostaw wersję z większą liczbą słów
        merged[i] = bWords.length >= aWords.length ? t : merged[i]
        handled = true
        break
      }
    }
    if (!handled) merged.push(t)
  }
  return merged.join(' ')
}

// Niektóre implementacje SpeechRecognition (szczególnie Android/Chrome w trybie
// continuous=true, w hałaśliwym otoczeniu bez odszumiania) potrafią wielokrotnie
// "zapętlić się" na tym samym słowie/frazie, generując dziesiątki powtórzeń
// (np. "podajnik podajnik podajnik numer podajnik numer 72 ..."). To błąd silnika
// rozpoznawania telefonu, nie logiki aplikacji — tu tylko ograniczamy szkody,
// usuwając powtarzające się sąsiadujące frazy (1-4 słowa) z finalnego tekstu.
export function collapseRepeatedPhrases(text: string): string {
  if (!text) return text
  let words = text.split(/\s+/).filter(Boolean)

  let changed = true
  let guard = 0
  while (changed && guard < 25) {
    changed = false
    guard++
    // Górna granica długości frazy do porównania — dawniej sztywne 4 słowa, co łapało
    // tylko powtórzenia pojedynczych słów/krótkich fraz, a nie CAŁYCH zdań (silnik
    // rozpoznawania telefonu potrafi zapętlić się na całym, kilkunastowyrazowym zdaniu).
    // Liczone od aktualnej (już częściowo skróconej) długości tekstu.
    const maxN = Math.max(4, Math.min(20, Math.floor(words.length / 2)))
    for (let n = 1; n <= maxN; n++) {
      const result: string[] = []
      let i = 0
      while (i < words.length) {
        if (i + 2 * n <= words.length) {
          const a = words.slice(i, i + n).join(' ').toLowerCase()
          const b = words.slice(i + n, i + 2 * n).join(' ').toLowerCase()
          if (a === b) {
            result.push(...words.slice(i, i + n))
            let j = i + n
            while (j + n <= words.length && words.slice(j, j + n).join(' ').toLowerCase() === a) {
              j += n
            }
            i = j
            changed = true
            continue
          }
        }
        result.push(words[i])
        i++
      }
      words = result
    }
  }
  return words.join(' ')
}
